import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { MarkdownComponent } from 'ngx-markdown';
import { DOCK_RENDER_ZONE } from '@shared/floating-panel/dock-render-zone.token';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import {
  FLOATING_PANEL_HEADER_ACTIONS,
  FloatingPanelHeaderAction,
} from '@shared/floating-panel/floating-panel-header-actions.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { HdCenterMiniCardComponent } from '../basic-properties/hd-center-mini-card.component';
import { HdDocPanelService } from '../doc-panel/hd-doc-panel.service';
import { HdDocRef } from '../doc-panel/hd-doc-ref.model';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import {
  HdCenterInfoSection,
  HdCenterInfoStateSection,
} from './hd-center-info-content.manifest';
import { HdCenterInfoRequestedContext } from './hd-center-info-requested-context.model';
import { HdCenterInfoService } from './hd-center-info.service';
import { HdFearsPanelService } from '../../fears/hd-fears-panel.service';
import { hdFearCenterForBodygraphCenter } from '../../fears/hd-fears.metadata';
import { HdVoicesPanelService } from '../../voices/hd-voices-panel.service';
import { HdActivePaletteService } from '../hd-active-palette.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdEmotionalWavesListComponent } from '../../emotional-waves/hd-emotional-waves-list.component';

const CENTER_NAMES_TRANSLATION_KEY = 'humanDesign.properties.centerNames';
const CENTER_INFO_TRANSLATION_KEY = 'humanDesign.centerInfo';

@Component({
  selector: 'hd-center-info-requested',
  templateUrl: './hd-center-info-requested.component.html',
  styleUrl: './hd-center-info-requested.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdCenterMiniCardComponent, HdEmotionalWavesListComponent, MarkdownComponent],
  host: {
    '[class.hd-center-info-host--full]': 'isFull()',
    '[class.hd-center-info-host--dock-full]': 'isDockFull',
  },
})
export class HdCenterInfoRequestedComponent {
  readonly context = input<HdCenterInfoRequestedContext | null>(null);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdCenterInfoRequestedContext>
    | null;
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });
  private readonly headerActions = inject(FLOATING_PANEL_HEADER_ACTIONS, { optional: true });
  private readonly dockRenderZone = inject(DOCK_RENDER_ZONE, { optional: true });
  private readonly centerInfo = inject(HdCenterInfoService);
  private readonly centerColors = inject(HdActivePaletteService);
  private readonly docPanel = inject(HdDocPanelService);
  private readonly fearsPanel = inject(HdFearsPanelService);
  private readonly voicesPanel = inject(HdVoicesPanelService);
  private readonly contentLanguage = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly transloco = inject(TranslocoService);

  protected readonly effectiveContext = computed<HdCenterInfoRequestedContext | null>(() => {
    const context = this.context() ?? this.panelData?.() ?? null;
    return context?.kind === 'center-requested' ? context : null;
  });

  protected readonly panelMode = signal<'compact' | 'full'>('compact');
  protected readonly showingOverview = signal(false);
  private readonly sectionTitlesByUrl = signal<Readonly<Record<string, string>>>({});
  private readonly overviewBackButton = viewChild<ElementRef<HTMLButtonElement>>('overviewBackButton');
  private readonly overviewLinkButton = viewChild<ElementRef<HTMLButtonElement>>('overviewLinkButton');
  private readonly pendingViewSwapFocus = signal<'overview' | 'center' | null>(null);
  private panelModeInitialized = false;
  protected readonly isDockFull = this.dockRenderZone === 'full';
  protected readonly isFull = computed(() => this.isDockFull || this.panelMode() === 'full');

  private readonly language = computed(() => {
    this.contentLanguage.translationsLoadedTick();
    return this.contentLanguage.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly center = computed(() => this.effectiveContext()?.center ?? null);
  protected readonly isDefined = computed(() => this.effectiveContext()?.isDefined ?? false);
  protected readonly isOpen = computed(() => this.effectiveContext()?.isOpen ?? false);
  protected readonly variant = computed<'defined' | 'undefined'>(() =>
    this.isDefined() ? 'defined' : 'undefined',
  );
  protected readonly colorBlocks = computed<readonly string[]>(() => {
    const center = this.center();
    return center ? [this.centerColors.color(center)] : [];
  });
  protected readonly centerAccent = computed(() => {
    const center = this.center();
    return center ? this.centerColors.color(center) : 'var(--amber)';
  });

  protected readonly overviewUrl = computed(() =>
    this.centerInfo.overviewMarkdownUrl(this.language()),
  );
  protected readonly generalUrl = computed(() => this.sectionUrl('general'));
  protected readonly currentStateSection = computed<HdCenterInfoStateSection>(() =>
    this.isDefined() ? 'defined' : 'undefined',
  );
  protected readonly alternateStateSection = computed<HdCenterInfoStateSection>(() =>
    this.isDefined() ? 'undefined' : 'defined',
  );
  protected readonly currentStateUrl = computed(() => this.sectionUrl(this.currentStateSection()));
  protected readonly alternateStateUrl = computed(() => this.sectionUrl(this.alternateStateSection()));
  protected readonly cartographyUrl = computed(() => this.sectionUrl('cartography'));

  protected readonly overviewLabel = computed(() => this.translate('overview'));
  protected readonly overviewLinkLabel = computed(() => this.translate('overviewLink'));
  protected readonly fearsLinkLabel = computed(() => this.translate('fearsLink'));

  /** Angsten-doorklik enkel voor de drie bewustzijnscentra (milt/ajna/solar plexus). */
  protected readonly fearsCenter = computed(() => {
    const center = this.center();
    return center ? hdFearCenterForBodygraphCenter(center) : null;
  });

  protected openFears(event: MouseEvent): void {
    const fears = this.fearsCenter();
    if (fears == null) {
      return;
    }
    this.fearsPanel.openForCenter(fears.key, { x: event.clientX, y: event.clientY });
  }

  protected readonly voicesLinkLabel = computed(() => this.translate('voicesLink'));

  /** Voices-doorklik enkel voor het keelcentrum. */
  protected readonly voicesAvailable = computed(() => this.center() === 'keel');

  /** Sectie met de emotionele golven enkel voor de Solar Plexus. */
  protected readonly emotionalWavesAvailable = computed(() => this.center() === 'solarplexus');

  protected readonly emotionalWavesLabel = computed(() => {
    const lang = this.language();
    return this.gateInfo.cheatSheetTextIn('emotionalWaves', 'panel.title', lang)
      || this.gateInfo.cheatSheetTextIn('emotionalWaves', 'panel.title', 'nl');
  });

  protected openVoices(event: MouseEvent): void {
    if (!this.voicesAvailable()) {
      return;
    }
    this.voicesPanel.openForThroat({ x: event.clientX, y: event.clientY });
  }
  protected readonly generalLabel = computed(() => this.translate('general'));
  protected readonly currentStateLabel = computed(() => this.stateLabel(this.currentStateSection()));
  protected readonly alternateStateLabel = computed(() => this.stateLabel(this.alternateStateSection()));
  protected readonly cartographyLabel = computed(() => this.translate('cartography'));
  protected readonly currentBadgeLabel = computed(() => this.translate('currentState'));
  protected readonly compactLabel = computed(() => this.translate('compact'));
  protected readonly fullLabel = computed(() => this.translate('full'));
  protected readonly backToCenterLabel = computed(() => this.transloco.translate(
    `${CENTER_INFO_TRANSLATION_KEY}.backToCenter`,
    { center: this.centerName() },
    this.language(),
  ));

  constructor() {
    effect(() => {
      const context = this.effectiveContext();
      if (!context || this.panelModeInitialized) return;
      this.panelModeInitialized = true;
      this.panelMode.set(context.initialMode ?? 'compact');
    });

    effect(() => {
      const centerName = this.centerName();
      if (centerName) this.panelRef?.setDockTabLabel(centerName);
    });

    effect(() => {
      if (!this.headerActions) return;
      if (this.isDockFull || this.showingOverview()) {
        this.headerActions.set([]);
        return;
      }
      const action: FloatingPanelHeaderAction = this.isFull()
        ? {
          icon: 'down-left-and-up-right-to-center',
          tooltip: this.compactLabel(),
          onClick: () => this.showCompactInfo(),
        }
        : {
          icon: 'up-right-and-down-left-from-center',
          tooltip: this.fullLabel(),
          onClick: () => this.showFullInfo(),
        };
      this.headerActions.set([action]);
    });

    effect(() => {
      const focusTarget = this.pendingViewSwapFocus();
      if (!focusTarget) return;

      const button = focusTarget === 'overview'
        ? this.overviewBackButton()?.nativeElement
        : this.overviewLinkButton()?.nativeElement;
      if (!button) return;

      button.focus();
      this.pendingViewSwapFocus.set(null);
    });
  }

  protected showFullInfo(): void {
    this.panelMode.set('full');
    this.panelRef?.nudgeIntoView();
  }

  protected showCompactInfo(): void {
    this.panelMode.set('compact');
  }

  protected openOverview(): void {
    const overviewRef: HdDocRef = {
      kind: 'markdown',
      url: this.overviewUrl(),
      title: this.overviewLabel(),
    };
    if (this.docPanel.linkOpenMode() === 'new-panel') {
      this.docPanel.openDocPanel(overviewRef);
      return;
    }

    this.showingOverview.set(true);
    this.pendingViewSwapFocus.set('overview');
    this.panelRef?.nudgeIntoView();
  }

  protected backToCenter(): void {
    this.showingOverview.set(false);
    this.pendingViewSwapFocus.set('center');
    this.panelRef?.nudgeIntoView();
  }

  /** Hermeet het floating paneel nadat ngx-markdown zijn asynchrone render afrondde. */
  protected onMarkdownRendered(): void {
    this.panelRef?.nudgeIntoView();
  }

  protected onSectionMarkdownLoaded(url: string, markdown: string): void {
    const heading = this.firstLevelTwoHeading(markdown);
    if (url && heading) {
      this.sectionTitlesByUrl.update(titles => {
        if (titles[url] === heading) return titles;
        return { ...titles, [url]: heading };
      });
    }

    this.panelRef?.nudgeIntoView();
  }

  protected sectionTitle(url: string, fallback: string): string {
    return this.sectionTitlesByUrl()[url] ?? fallback;
  }

  private centerName(): string {
    const center = this.center();
    if (!center) return '';
    return this.transloco.translate(
      `${CENTER_NAMES_TRANSLATION_KEY}.${center}`,
      undefined,
      this.language(),
    );
  }

  private sectionUrl(section: HdCenterInfoSection): string {
    const center = this.center();
    return center ? this.centerInfo.centerMarkdownUrl(center, section, this.language()) : '';
  }

  private stateLabel(section: HdCenterInfoStateSection): string {
    if (section === 'defined') return this.translate('defined');
    return this.isOpen() ? this.translate('open') : this.translate('undefined');
  }

  private firstLevelTwoHeading(markdown: string): string | null {
    const heading = /^##(?!#)[\t ]+(.+?)[\t ]*$/m.exec(markdown)?.[1]?.trim();
    return heading || null;
  }

  private translate(key: string): string {
    return this.transloco.translate(
      `${CENTER_INFO_TRANSLATION_KEY}.${key}`,
      undefined,
      this.language(),
    );
  }
}
